import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as orgApi from './api/organizations'
import './Login.css'

function Login() {
  const { user, login, register } = useAuth()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')

  const [authMode, setAuthMode] = useState(inviteToken ? 'signup' : 'login')
  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    fullName: '',
  })
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState('')
  const navigate = useNavigate()

  // Invitation verification state
  const [invitation, setInvitation] = useState(null)
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken)
  const [inviteError, setInviteError] = useState('')

  // Verify invitation token if present
  useEffect(() => {
    if (inviteToken) {
      verifyInvitation(inviteToken)
    }
  }, [inviteToken])

  const verifyInvitation = async (token) => {
    setInviteLoading(true)
    setInviteError('')

    try {
      const result = await orgApi.verifyInvitationToken(token)
      setInvitation(result)
      // Pre-fill the email from the invitation
      setAuthData((prev) => ({ ...prev, email: result.email }))
      // Ensure we're in signup mode
      setAuthMode('signup')
    } catch (err) {
      console.error('Failed to verify invitation:', err)
      if (err.status === 410) {
        setInviteError(
          'This invitation link has already been used or has expired. Please request a new invitation.'
        )
      } else if (err.status === 404) {
        setInviteError(
          'Invalid invitation link. Please check the link or request a new invitation.'
        )
      } else {
        setInviteError(err.message || 'Failed to verify invitation link')
      }
    } finally {
      setInviteLoading(false)
    }
  }

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard')
    }
  }, [user, navigate])

  const handleAuthInputChange = (e) => {
    setAuthData({
      ...authData,
      [e.target.name]: e.target.value,
    })
    setAuthError('')
  }

  const handleSignUp = async (e) => {
    e.preventDefault()
    setAuthLoading(true)
    setAuthError('')

    try {
      if (!authData.email || !authData.password) {
        throw new Error('Please fill in all required fields')
      }

      if (authData.password.length < 8) {
        throw new Error('Password must be at least 8 characters')
      }

      await register(authData.email, authData.password, authData.fullName || null)

      // If signed up via invite, go to organization settings
      // Otherwise, go to pricing to create/select an organization
      if (invitation) {
        navigate('/organization?welcome=true')
      } else {
        navigate('/pricing?welcome=true')
      }
    } catch (err) {
      setAuthError(err.message || 'Failed to create account')
    } finally {
      setAuthLoading(false)
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setAuthLoading(true)
    setAuthError('')

    try {
      if (!authData.email || !authData.password) {
        throw new Error('Please enter email and password')
      }

      await login(authData.email, authData.password)

      navigate('/dashboard')
    } catch (err) {
      setAuthError(err.message || 'Failed to log in')
    } finally {
      setAuthLoading(false)
    }
  }

  return (
    <div className="login-page-wrapper">
      <div className="login-container">
        <div className="login-header">
          <Link to="/" className="login-logo">
            <span className="logo-text">SurveyTip</span>
          </Link>
          <p className="login-subtitle">
            {authMode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        <div className="login-form-container">
          {/* Invitation Banner */}
          {inviteLoading && (
            <div className="invite-banner invite-loading">
              <p>Verifying invitation link...</p>
            </div>
          )}

          {inviteError && (
            <div className="invite-banner invite-error">
              <p>{inviteError}</p>
            </div>
          )}

          {invitation && !inviteError && (
            <div className="invite-banner invite-success">
              <p>
                You've been invited to join <strong>{invitation.organization_name}</strong> as a{' '}
                <strong>{invitation.role_code.replace('_', ' ')}</strong>
              </p>
              <p className="invite-subtitle">
                Complete your signup below to accept the invitation.
              </p>
            </div>
          )}

          {/* Hide toggle if there's an active invitation */}
          {!invitation && (
            <div className="auth-toggle">
              <button
                className={`toggle-btn ${authMode === 'login' ? 'active' : ''}`}
                onClick={() => setAuthMode('login')}>
                Sign In
              </button>
              <button
                className={`toggle-btn ${authMode === 'signup' ? 'active' : ''}`}
                onClick={() => setAuthMode('signup')}>
                Get Started
              </button>
            </div>
          )}

          {authError && <div className="auth-error">{authError}</div>}

          <form onSubmit={authMode === 'signup' ? handleSignUp : handleLogin}>
            {authMode === 'signup' && (
              <input
                type="text"
                name="fullName"
                placeholder="Full Name (optional)"
                value={authData.fullName}
                onChange={handleAuthInputChange}
              />
            )}

            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={authData.email}
              onChange={handleAuthInputChange}
              required
              readOnly={!!invitation}
              style={invitation ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
            />

            <input
              type="password"
              name="password"
              placeholder="Password"
              value={authData.password}
              onChange={handleAuthInputChange}
              required
              minLength={8}
            />

            {authMode === 'signup' && (
              <p className="password-hint">Password must be at least 8 characters</p>
            )}

            <button type="submit" disabled={authLoading} className="auth-submit">
              {authLoading
                ? 'Please wait...'
                : authMode === 'signup'
                  ? 'Create Account'
                  : 'Sign In'}
            </button>
          </form>

          <div className="login-footer">
            <Link to="/" className="back-home">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
